/**
 * ConverterQualityReport — detailed reconciliation / final-table Data
 * Quality scorecard. Renders the per-table completeness / consistency /
 * uniqueness / row-count-parity metrics `reconcile_detailed` and
 * `final-table-dq` compute (see ztd_app/api/modules/data_converter/
 * service.py's `_score_table_quality`), plus an overall project score.
 *
 * Rendered as one scannable table (not a stacked card list) so a
 * non-technical reviewer can compare every table's scores side by side at
 * a glance, and can save the whole report off as a PDF or Excel workbook
 * instead of only ever reading it on-screen.
 *
 * Purely presentational — the caller supplies already-fetched
 * `TableQualityResult[]` (from ConverterReconcileDetailedOut.tables or
 * ConverterFinalTableDqOut.report.tables).
 */
import { AlertTriangle, CheckCircle2, Download, XCircle } from "lucide-react";
import type { TableQualityResult } from "../apiv2";
import { downloadTableAsExcel, downloadTableAsPdf } from "../lib/tableExport";
import { severityFromScore } from "./discovery/ScoreHero";

// Same 5-tier ladder as the Profiler's ScoreHero/severityFromScore — a score
// of 65 must mean the same thing ("moderate") in both agents' UIs, not a
// 3-tier reimplementation here that reads a different severity for the same
// number.
function scoreColor(score: number): string {
  switch (severityFromScore(score)) {
    case "excellent":
      return "text-readiness-excellent";
    case "good":
      return "text-readiness-good";
    case "moderate":
      return "text-readiness-moderate";
    case "poor":
      return "text-readiness-poor";
    default:
      return "text-readiness-critical";
  }
}

function scoreBg(score: number): string {
  switch (severityFromScore(score)) {
    case "excellent":
      return "bg-readiness-excellent/15";
    case "good":
      return "bg-readiness-good/15";
    case "moderate":
      return "bg-readiness-moderate/15";
    case "poor":
      return "bg-readiness-poor/15";
    default:
      return "bg-readiness-critical/15";
  }
}

function pct(value: number | null): string {
  return value === null || value === undefined ? "—" : `${value}%`;
}

const REPORT_COLUMNS = [
  "Source Table",
  "Target Table",
  "Score",
  "Completeness",
  "Consistency",
  "Uniqueness",
  "Source Rows",
  "Target Rows",
  "Row Counts",
  "Issues",
];

function reportRows(tables: TableQualityResult[]): (string | number)[][] {
  return tables.map((t) => [
    t.source_table,
    t.target_table,
    Math.round(t.overall_score),
    pct(t.completeness_pct),
    pct(t.consistency_pct),
    pct(t.uniqueness_pct),
    t.source_count ?? "—",
    t.target_count ?? "—",
    t.error ? "Error" : t.row_count_match ? "Match" : "Mismatch",
    t.error ??
      (t.validity_issues.length > 0 ? t.validity_issues.join("; ") : "None"),
  ]);
}

export function ConverterQualityReport({
  tables,
  overallScore,
  title = "Data Quality Report",
}: {
  tables: TableQualityResult[];
  overallScore: number;
  title?: string;
}) {
  const tablesWithIssues = tables.filter(
    (t) => t.validity_issues.length > 0 || !t.row_count_match,
  );
  const safeFileBase = title.toLowerCase().replace(/[^a-z0-9]+/g, "_");

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center gap-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {tables.length} table(s) · {tablesWithIssues.length} with issue(s)
            </p>
          </div>
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-full ${scoreBg(overallScore)}`}
          >
            <span className={`text-lg font-bold ${scoreColor(overallScore)}`}>
              {Math.round(overallScore)}
            </span>
          </div>
        </div>
        {tables.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                downloadTableAsPdf(
                  title,
                  REPORT_COLUMNS,
                  reportRows(tables),
                  `${safeFileBase}.pdf`,
                )
              }
              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
              title="Download this report as a PDF"
            >
              <Download size={12} /> PDF
            </button>
            <button
              onClick={() =>
                downloadTableAsExcel(
                  title.slice(0, 31),
                  REPORT_COLUMNS,
                  reportRows(tables),
                  `${safeFileBase}.xls`,
                )
              }
              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
              title="Download this report as an Excel workbook"
            >
              <Download size={12} /> Excel
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <th className="px-3 py-2 text-left font-medium">Table</th>
              <th className="px-3 py-2 text-center font-medium">Score</th>
              <th className="px-3 py-2 text-center font-medium">
                Completeness
              </th>
              <th className="px-3 py-2 text-center font-medium">Consistency</th>
              <th className="px-3 py-2 text-center font-medium">Uniqueness</th>
              <th className="px-3 py-2 text-center font-medium">Row Counts</th>
              <th className="px-3 py-2 text-left font-medium">Issues</th>
            </tr>
          </thead>
          <tbody>
            {tables.map((t) => (
              <tr
                key={`${t.source_table}->${t.target_table}`}
                className="border-t border-slate-100 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    {t.error ? (
                      <XCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
                    ) : t.row_count_match && t.validity_issues.length === 0 ? (
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                    )}
                    <span className="font-medium text-slate-800 dark:text-slate-100">
                      {t.source_table} → {t.target_table}
                    </span>
                  </div>
                </td>
                <td
                  className={`px-3 py-2 text-center font-bold ${scoreColor(t.overall_score)}`}
                >
                  {Math.round(t.overall_score)}
                </td>
                <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-200">
                  {pct(t.completeness_pct)}
                </td>
                <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-200">
                  {pct(t.consistency_pct)}
                </td>
                <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-200">
                  {pct(t.uniqueness_pct)}
                </td>
                <td className="px-3 py-2 text-center">
                  {t.error ? (
                    <span className="text-red-500">Error</span>
                  ) : (
                    <span
                      className={
                        t.row_count_match ? "text-green-500" : "text-amber-500"
                      }
                    >
                      {t.source_count ?? "?"} / {t.target_count ?? "?"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                  {t.error ? (
                    <span className="text-red-600 dark:text-red-400">
                      {t.error}
                    </span>
                  ) : t.validity_issues.length === 0 ? (
                    <span className="text-slate-400">None</span>
                  ) : (
                    <span title={t.validity_issues.join("\n")}>
                      {t.validity_issues.slice(0, 2).join(", ")}
                      {t.validity_issues.length > 2 &&
                        ` (+${t.validity_issues.length - 2} more)`}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
